import React from 'react';
import { Menu } from 'lucide-react';

export interface TopBarProps {
  // Desktop navigation
  breadcrumbs?: React.ReactNode;

  // Mobile branding (hidden on desktop)
  logo?: React.ReactNode;
  title?: string; // View name

  // Custom buttons (qs2 pattern)
  rightButtons?: React.ReactNode;

  // User controls (qsf pattern)
  userMenu?: React.ReactNode;
  languageSelector?: React.ReactNode;

  // Menu toggle
  onMenuClick: () => void;

  // Additional className
  className?: string;
}

export const TopBar: React.FC<TopBarProps> = ({
  breadcrumbs,
  logo,
  title,
  rightButtons,
  userMenu,
  languageSelector,
  onMenuClick,
  className = '',
}) => {
  return (
    <header
      className={`h-[64px] sticky top-0 z-20 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 ${className}`}
      data-id="top-bar"
    >
      {/* Height adjusts via media query: taller on mobile for stacked content */}
      <div className="flex items-center justify-between px-4 min-h-16 h-auto h-20 lg:h-16">
        {/* Left section */}
        <div className="flex items-center gap-3">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-foreground-secondary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle menu"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Desktop breadcrumbs (hidden on mobile) */}
          <div className="hidden lg:flex items-center gap-2">{breadcrumbs}</div>

          {/* Mobile logo/title - responsive layout via media queries, hidden on desktop */}
          {(logo || title) && (
            <div className="flex lg:hidden absolute left-1/2 -translate-x-1/2 flex-col sm:flex-row items-center gap-1 sm:gap-2 py-2">
              {logo}
              {title && (
                <span className="text-sm sm:text-lg font-medium sm:font-semibold text-foreground whitespace-nowrap">
                  {title}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {rightButtons}
          {languageSelector}
          {userMenu}
        </div>
      </div>
    </header>
  );
};
